
import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { UserPlus, Loader2 } from "lucide-react";
import { InputWithSuggestions } from "@/components/ui/input-with-suggestions";
import { useClientData } from "@/domains/clients/hooks/useClientData";
import { Client } from "@/types/entities/client";
import { dbClientToClient } from "@/utils/transformers/clientTransformers";

interface EnhancedClientSelectorProps {
  selectedClientId: string;
  clients: Client[];
  onClientChange: (clientId: string) => void;
  onAddClient: () => void;
  isLoading?: boolean;
}

export const EnhancedClientSelector = ({
  selectedClientId,
  clients: propClients,
  onClientChange,
  onAddClient,
  isLoading: propLoading = false
}: EnhancedClientSelectorProps) => {
  type ClientSuggestion = {
    id: string;
    value: string;
    label: string;
    description?: string;
  };
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  const { clients: hookClients, isLoading: hookLoading } = useClientData();
  
  // Используем клиентов из хука, если переданные клиенты пусты
  // CRITICAL DEBUG: Track data source
  console.log('[EnhancedClientSelector] DATA SOURCE DEBUG:');
  console.log('[EnhancedClientSelector] hookClients length:', hookClients?.length || 0);
  console.log('[EnhancedClientSelector] propClients length:', propClients?.length || 0);
  console.log('[EnhancedClientSelector] hookClients sample:', hookClients?.[0]);
  console.log('[EnhancedClientSelector] propClients sample:', propClients?.[0]);
  
  const rawClients = hookClients && hookClients.length > 0 ? hookClients : propClients || [];
  const dataSource = hookClients && hookClients.length > 0 ? 'hookClients' : 'propClients';
  // Important: only consider hookLoading when we actually use hookClients as data source.
  // Otherwise, rely solely on the provided propLoading to avoid perpetual disabled state.
  // If hookClients are the source and already contain data -> never block UI.
  // Only when hookClients are the intended source but empty, respect hookLoading/propLoading.
  const usingHook = dataSource === 'hookClients';
  const hookHasData = (hookClients?.length ?? 0) > 0;
  const actualLoading = usingHook
    ? (hookHasData ? false : (hookLoading || Boolean(propLoading)))
    : Boolean(propLoading);
  
  console.log('[EnhancedClientSelector] USING DATA SOURCE:', dataSource);
  console.log('[EnhancedClientSelector] Clients count:', rawClients.length);
  
  // Логируем трансформированные данные клиентов для диагностики
  console.log('[EnhancedClientSelector] Clients count:', rawClients.length);
  rawClients.forEach((client, index) => {
    console.log(`[EnhancedClientSelector] Client ${index + 1}:`, {
      id: client.id,
      fullName: client.fullName,
      companyName: client.companyName,
      phone: client.phone,
      email: client.email,
      type: client.type
    });
  });
  
  // rawClients уже являются трансформированными Client объектами
  const unifiedClients: Client[] = rawClients.map((client: Client) => {
    console.log('[EnhancedClientSelector] Using client directly:', {
      id: client.id,
      fullName: client.fullName,
      companyName: client.companyName,
      phone: client.phone,
      email: client.email,
      type: client.type
    });
    return client;
  });
  
  // Find selected client name
  const selectedClient = unifiedClients.find((client: Client) => client.id === selectedClientId);
  const selectedClientName = selectedClient?.fullName || selectedClient?.companyName || "";
  
  console.log('[EnhancedClientSelector] Debug info:', {
    hookClients: hookClients?.length || 0,
    propClients: propClients?.length || 0,
    unifiedClients: unifiedClients?.length || 0,
    hookLoading,
    propLoading,
    actualLoading,
    showSuggestions,
    searchQuery,
    selectedClientId,
    selectedClientName
  });
  
  // Детальное логирование для диагностики проблемы
  console.log('[EnhancedClientSelector] Client selection debug:', {
    selectedClientId,
    unifiedClientsIds: unifiedClients.map(c => ({ id: c.id, name: c.fullName || c.companyName })),
    foundClient: selectedClient,
    allClients: unifiedClients
  });
  
  // Создаем предложения на основе поискового запроса
  const suggestions = React.useMemo(() => {
    if (!searchQuery || searchQuery.length < 1) {
      return [];
    }

    const query = searchQuery.toLowerCase();
    return unifiedClients
      .filter((client: Client) => {
        const name = (client.fullName || client.companyName || '').toLowerCase();
        const phone = (client.phone || '').toLowerCase();
        const email = (client.email || '').toLowerCase();
        
        return name.includes(query) || phone.includes(query) || email.includes(query);
      })
      .map((client: Client) => ({
        id: client.id,
        value: client.fullName || client.companyName || 'Без имени',
        label: client.fullName || client.companyName || 'Без имени',
        description: client.phone ? `📱 ${client.phone}` : undefined
      }))
      .slice(0, 10);
  }, [unifiedClients, searchQuery]);
  
  // Дополнительное логирование для отладки
  if (selectedClientId && !selectedClient) {
    console.warn('[EnhancedClientSelector] WARNING: Client not found!', {
      selectedClientId,
      availableClientIds: unifiedClients.map(c => c.id),
      unifiedClients
    });
  }
  
  if (selectedClient) {
    console.log('[EnhancedClientSelector] Found selected client:', {
      id: selectedClient.id,
      name: selectedClient.fullName || selectedClient.companyName,
      phone: selectedClient.phone,
      email: selectedClient.email,
      type: selectedClient.type
    });
  }

  const handleClientSelect = (suggestion: ClientSuggestion) => {
    console.log('[EnhancedClientSelector] Client selected:', suggestion);
    onClientChange(suggestion.id);
    setSearchQuery(suggestion.value);
    setShowSuggestions(false);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setShowSuggestions(value.length > 0);
    // Сбрасываем выбранного клиента только если поле полностью очищено
    if (!value) {
      onClientChange("");
    }
  };

  // Initialize search query with selected client name
  React.useEffect(() => {
    if (selectedClientName && !searchQuery) {
      setSearchQuery(selectedClientName);
      setShowSuggestions(false);
    }
  }, [selectedClientName, searchQuery]);

  // Обновляем поле поиска при изменении выбранного клиента
  React.useEffect(() => {
    if (selectedClientId && selectedClientName && searchQuery !== selectedClientName) {
      setSearchQuery(selectedClientName);
      setShowSuggestions(false);
    }
  }, [selectedClientId, selectedClientName, searchQuery]);

  // Автоматически показываем предложения при фокусе на поле ввода
  const handleInputFocus = () => {
    if (searchQuery.length > 0 && suggestions.length > 0 && !selectedClientId) {
      setShowSuggestions(true);
    }
  };

  // Показываем предложения если есть поисковый запрос и нет выбранного клиента
  const shouldShowSuggestions = showSuggestions && suggestions.length > 0 && !selectedClientId;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <InputWithSuggestions
            id="client-search"
            label="Клиент"
            value={searchQuery}
            onChange={handleSearchChange}
            onSelect={handleClientSelect}
            suggestions={shouldShowSuggestions ? suggestions : []}
            loading={actualLoading}
            placeholder="Начните вводить имя клиента..."
            disabled={actualLoading}
          />
        </div>
        <Button 
          type="button" 
          variant="outline" 
          size="sm" 
          onClick={onAddClient}
          disabled={actualLoading}
          className="mt-6 bg-primary text-primary-foreground hover:bg-primary/90"
        >
          {actualLoading ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <UserPlus className="h-4 w-4 mr-1" />
          )}
          Добавить
        </Button>
      </div>
      
      {selectedClientId && selectedClient && (
        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <p className="text-sm font-medium text-green-800">
              Клиент выбран
            </p>
          </div>
          <p className="text-sm font-semibold text-gray-900">
            {selectedClientName}
          </p>
          {selectedClient.phone && (
            <p className="text-xs text-gray-600 mt-1">
              📱 {selectedClient.phone}
            </p>
          )}
          {selectedClient.email && (
            <p className="text-xs text-gray-600">
              ✉️ {selectedClient.email}
            </p>
          )}
          {selectedClient.type && (
            <p className="text-xs text-gray-500 mt-1">
              {selectedClient.type === 'individual' ? 'Физическое лицо' : 'Юридическое лицо'}
            </p>
          )}
        </div>
      )}
      
      <div className="text-xs text-muted-foreground">
        <p>Клиентов загружено: {unifiedClients.length}</p>
        <p>Загрузка: {actualLoading ? 'Да' : 'Нет'}</p>
        <p>Найдено предложений: {suggestions.length}</p>
        <p>Поиск: "{searchQuery}"</p>
        <p>Показывать предложения: {shouldShowSuggestions ? 'Да' : 'Нет'}</p>
      </div>
    </div>
  );
};
